import { useState, useCallback } from "react";
import { adminApi } from "../repositories/admin";
import Swal from "sweetalert2";

interface AppUpdate {
  id: number;
  title: string;
  updateData: string;
  views: number;
  status: "published" | "draft";
  createdAt: string;
  updatedAt: string;
}

export const useAppUpdate = () => {
  const [appUpdates, setAppUpdates] = useState<AppUpdate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Fetch all app updates
  const fetchAppUpdates = useCallback(async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    searchType?: string;
  }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.getAllAppUpdates(params);
      if (response?.data?.success) {
        setAppUpdates(response.data.data || []);
        if (response.data.pagination) {
          setPagination({
            currentPage: response.data.pagination.page || 1,
            totalPages: response.data.pagination.totalPages || 1,
            totalItems: response.data.pagination.totalItems || 0,
            itemsPerPage: response.data.pagination.limit || 10,
          });
        }
        return response.data;
      }
      throw new Error("Failed to fetch app updates");
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || "Failed to fetch app updates";
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new app update (draft)
  const createDraft = useCallback(async (title: string, content: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.draftAppUpdate({ title, updateData: content });
      await fetchAppUpdates();

      Swal.fire({
        title: "Success!",
        text: response?.data?.message || "Update saved as draft!",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      return response.data;
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || "Failed to save draft";
      setError(errorMessage);

      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
      });

      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchAppUpdates]);

  // Publish a new app update
  const publishUpdate = useCallback(async (title: string, content: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.addAppUpdate({ title, updateData: content });
      await fetchAppUpdates();

      Swal.fire({
        title: "Success!",
        text: response?.data?.message || "Update published successfully!",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      return response.data;
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || "Failed to publish update";
      setError(errorMessage);

      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
      });

      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchAppUpdates]);

  // Update an existing app update
  const updateAppUpdate = useCallback(async (
    id: number,
    title: string,
    content: string,
    status: "published" | "draft"
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.updateAppUpdate(id, {
        title,
        updateData: content,
        status,
      });
      await fetchAppUpdates();

      Swal.fire({
        title: "Success!",
        text: response?.data?.message || "Update saved successfully!",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      return response.data;
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || "Failed to update";
      setError(errorMessage);

      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
      });

      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchAppUpdates]);

  // Delete an app update
  const deleteAppUpdate = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.deleteAppUpdate(id);
      await fetchAppUpdates();

      Swal.fire({
        title: "Deleted!",
        text: response?.data?.message || "Update deleted successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      return response.data;
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || "Failed to delete update";
      setError(errorMessage);

      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
      });

      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchAppUpdates]);

  return {
    appUpdates,
    loading,
    error,
    pagination,
    fetchAppUpdates,
    createDraft,
    publishUpdate,
    updateAppUpdate,
    deleteAppUpdate,
  };
};
